import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Plus, Send, Bell, Mail, MessageSquare,
  Phone, CheckCircle, XCircle, Clock, AlertTriangle, Filter,
  RotateCcw, FileText, Settings, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const ChannelIcon = ({ channel }) => {
  const icons = {
    whatsapp: <MessageSquare className="w-4 h-4 text-green-400" />,
    email: <Mail className="w-4 h-4 text-blue-400" />,
    sms: <Phone className="w-4 h-4 text-purple-400" />,
    push: <Bell className="w-4 h-4 text-amber-400" />,
    in_app: <Bell className="w-4 h-4 text-cyan-400" />,
  };
  return icons[channel] || <Bell className="w-4 h-4 text-slate-400" />;
};

const StatusBadge = ({ status }) => {
  const colors = {
    pending: 'bg-amber-600 text-amber-100',
    sent: 'bg-emerald-600 text-emerald-100',
    delivered: 'bg-green-600 text-green-100',
    failed: 'bg-red-600 text-red-100',
    bounced: 'bg-orange-600 text-orange-100',
    opted_out: 'bg-slate-500 text-slate-200',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-slate-600'}`}>
      {status}
    </span>
  );
};

export default function AdminNotificationsView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Modals
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Send form
  const [sendForm, setSendForm] = useState({
    notification_type: 'custom',
    recipient_email: '',
    recipient_phone: '',
    subject: '',
    message: '',
    channels: ['email'],
    priority: 'normal'
  });
  
  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    notification_type: 'custom',
    channel: 'email',
    subject: '',
    body: '',
    is_active: true
  });

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/notifications/list?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (channelFilter) url += `&channel=${channelFilter}`;
      if (typeFilter) url += `&notification_type=${typeFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error('Notifications fetch error:', err);
    }
  }, [statusFilter, channelFilter, typeFilter]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/templates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Templates fetch error:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchNotifications(), fetchTemplates()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchNotifications, fetchTemplates]);

  useEffect(() => {
    fetchNotifications();
  }, [statusFilter, channelFilter, typeFilter, fetchNotifications]);

  const handleSendNotification = async () => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sendForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowSendModal(false);
        setSendForm({
          notification_type: 'custom',
          recipient_email: '',
          recipient_phone: '',
          subject: '',
          message: '',
          channels: ['email'],
          priority: 'normal'
        });
        setSuccessMsg(`Notification sent: ${data.success ? 'Success' : 'Partial'} (${data.channels_attempted} channels)`);
        await Promise.all([fetchDashboard(), fetchNotifications()]);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to send');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleCreateTemplate = async () => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateForm)
      });
      
      if (res.ok) {
        setShowTemplateModal(false);
        setTemplateForm({
          name: '',
          notification_type: 'custom',
          channel: 'email',
          subject: '',
          body: '',
          is_active: true
        });
        setSuccessMsg('Template created');
        await fetchTemplates();
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to create template');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleRetry = async (notificationId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/notifications/${notificationId}/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        setSuccessMsg('Notification retried');
        await Promise.all([fetchDashboard(), fetchNotifications()]);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Retry failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Bell className="w-6 h-6 text-emerald-400" />
                Notification Center
              </h1>
              <p className="text-sm text-slate-400">Multi-channel notification management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dashboard?.pilot_mode && (
              <span className="px-3 py-1 bg-amber-900/50 border border-amber-700 rounded-full text-amber-300 text-xs font-medium">
                PILOT MODE
              </span>
            )}
            <Button 
              onClick={() => setShowSendModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="notification-send"
            >
              <Send className="w-4 h-4 mr-2" /> Send Notification
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Messages */}
        {successMsg && (
          <div className="bg-emerald-900/30 border border-emerald-700 rounded-xl p-4 mb-6">
            <p className="text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> {successMsg}
            </p>
          </div>
        )}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-red-400" onClick={() => setError('')}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-emerald-700/50 rounded-xl p-4"
            >
              <p className="text-emerald-400 text-xs mb-1">Sent Today</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.sent_today || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-red-700/50 rounded-xl p-4"
            >
              <p className="text-red-400 text-xs mb-1">Failed Today</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.failed_today || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-green-700/50 rounded-xl p-4"
            >
              <p className="text-green-400 text-xs mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> WhatsApp</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_channel?.whatsapp || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-blue-700/50 rounded-xl p-4"
            >
              <p className="text-blue-400 text-xs mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_channel?.email || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800 border border-purple-700/50 rounded-xl p-4"
            >
              <p className="text-purple-400 text-xs mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> SMS</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_channel?.sms || 0}</p>
            </motion.div>
          </div>
        )}

        {/* Config Status */}
        {dashboard && (
          <div className="flex gap-4 mb-6 text-xs">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${dashboard.email_configured ? 'bg-emerald-900/50 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
              <Mail className="w-3 h-3" />
              Email: {dashboard.email_configured ? 'Configured' : 'Not configured'}
            </div>
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${dashboard.sms_enabled ? 'bg-emerald-900/50 text-emerald-400' : 'bg-slate-800 text-slate-500'}`}>
              <Phone className="w-3 h-3" />
              SMS: {dashboard.sms_enabled ? 'Enabled' : 'Disabled'}
            </div>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-900/50 text-amber-400">
              <MessageSquare className="w-3 h-3" />
              WhatsApp: Mocked (Pilot)
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          <Button
            data-testid="notifications-tab-dashboard"
            variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
            className={activeTab === 'dashboard' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => setActiveTab('dashboard')}
          >
            <FileText className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <Button
            data-testid="notifications-tab-history"
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            className={activeTab === 'history' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => setActiveTab('history')}
          >
            <Bell className="w-4 h-4 mr-1" /> History ({notifications.length})
          </Button>
          <Button
            data-testid="notifications-tab-templates"
            variant={activeTab === 'templates' ? 'default' : 'ghost'}
            className={activeTab === 'templates' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => setActiveTab('templates')}
          >
            <Settings className="w-4 h-4 mr-1" /> Templates ({templates.length})
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Notifications */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-400" />
                Recent Notifications
              </h3>
              {dashboard.recent_notifications?.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {dashboard.recent_notifications.map((n) => (
                    <div key={n.id} className="bg-slate-900 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <ChannelIcon channel={n.channel} />
                          <span className="text-white text-sm">{n.notification_type?.replace(/_/g, ' ')}</span>
                        </div>
                        <StatusBadge status={n.status} />
                      </div>
                      <p className="text-slate-400 text-xs truncate">{n.recipient_email || n.recipient_phone}</p>
                      {n.subject && <p className="text-slate-500 text-xs truncate">{n.subject}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No recent notifications</p>
              )}
            </div>

            {/* Failed Notifications */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                Failed Notifications ({dashboard.failed_notifications?.length || 0})
              </h3>
              {dashboard.failed_notifications?.length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {dashboard.failed_notifications.map((n) => (
                    <div key={n.id} className="bg-slate-900 rounded-lg p-3 border border-red-900/50">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <ChannelIcon channel={n.channel} />
                          <span className="text-white text-sm">{n.notification_type?.replace(/_/g, ' ')}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-amber-400 px-2"
                          onClick={() => handleRetry(n.id)}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-slate-400 text-xs">{n.recipient_email || n.recipient_phone}</p>
                      {n.failure_reason && (
                        <p className="text-red-400 text-xs mt-1">{n.failure_reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-400 mb-2" />
                  <p className="text-emerald-400">No failed notifications</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Channels</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Types</option>
                <option value="authorization_created">Authorization Created</option>
                <option value="redemption_completed">Redemption Completed</option>
                <option value="support_case_created">Support Case Created</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {/* Notifications Table */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Channel</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Type</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Recipient</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Subject</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Status</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Created</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {notifications.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-slate-500 py-8">
                        No notifications found
                      </td>
                    </tr>
                  ) : (
                    notifications.map((n) => (
                      <tr key={n.id} className="hover:bg-slate-700/50">
                        <td className="px-4 py-3">
                          <ChannelIcon channel={n.channel} />
                        </td>
                        <td className="px-4 py-3 text-white text-sm">{n.notification_type?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-slate-400 text-sm">{n.recipient_email || n.recipient_phone || '-'}</td>
                        <td className="px-4 py-3 text-slate-400 text-sm truncate max-w-[150px]">{n.subject || '-'}</td>
                        <td className="px-4 py-3"><StatusBadge status={n.status} /></td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{new Date(n.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          {n.status === 'failed' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-400 px-2"
                              onClick={() => handleRetry(n.id)}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => setShowTemplateModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" /> New Template
              </Button>
            </div>

            {templates.length === 0 ? (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-8 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No notification templates yet</p>
                <Button
                  onClick={() => setShowTemplateModal(true)}
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  Create your first template
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((t) => (
                  <div key={t.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{t.name}</h4>
                      <div className="flex items-center gap-2">
                        <ChannelIcon channel={t.channel} />
                        {t.is_active ? (
                          <span className="px-2 py-0.5 bg-emerald-900/50 text-emerald-400 text-xs rounded">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded">Inactive</span>
                        )}
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{t.notification_type?.replace(/_/g, ' ')}</p>
                    {t.subject && <p className="text-slate-500 text-xs mb-1">Subject: {t.subject}</p>}
                    <p className="text-slate-500 text-xs line-clamp-2">{t.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Send Notification Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-emerald-400" />
                  Send Notification
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Type</label>
                    <select
                      value={sendForm.notification_type}
                      onChange={(e) => setSendForm(prev => ({ ...prev, notification_type: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="custom">Custom</option>
                      <option value="authorization_created">Authorization Created</option>
                      <option value="redemption_ready">Redemption Ready</option>
                      <option value="support_case_created">Support Case Created</option>
                      <option value="system_maintenance">System Maintenance</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Email</label>
                      <input
                        type="email"
                        value={sendForm.recipient_email}
                        onChange={(e) => setSendForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                        placeholder="user@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Phone</label>
                      <input
                        type="text"
                        value={sendForm.recipient_phone}
                        onChange={(e) => setSendForm(prev => ({ ...prev, recipient_phone: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                        placeholder="+18095551234"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Subject (for email)</label>
                    <input
                      type="text"
                      value={sendForm.subject}
                      onChange={(e) => setSendForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Message *</label>
                    <textarea
                      value={sendForm.message}
                      onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-24"
                      placeholder="Your notification message..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Channels</label>
                    <div className="flex gap-4">
                      {['whatsapp', 'email', 'sms'].map((ch) => (
                        <label key={ch} className="flex items-center gap-2 text-slate-400 text-sm">
                          <input
                            type="checkbox"
                            checked={sendForm.channels.includes(ch)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSendForm(prev => ({ ...prev, channels: [...prev.channels, ch] }));
                              } else {
                                setSendForm(prev => ({ ...prev, channels: prev.channels.filter(c => c !== ch) }));
                              }
                            }}
                            className="rounded"
                          />
                          {ch}
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Priority</label>
                    <select
                      value={sendForm.priority}
                      onChange={(e) => setSendForm(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowSendModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleSendNotification}
                    disabled={!sendForm.message || (!sendForm.recipient_email && !sendForm.recipient_phone)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Template Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  Create Template
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Name *</label>
                    <input
                      type="text"
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="Welcome Email"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Type</label>
                      <select
                        value={templateForm.notification_type}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, notification_type: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="custom">Custom</option>
                        <option value="authorization_created">Authorization Created</option>
                        <option value="redemption_ready">Redemption Ready</option>
                        <option value="account_created">Account Created</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Channel</label>
                      <select
                        value={templateForm.channel}
                        onChange={(e) => setTemplateForm(prev => ({ ...prev, channel: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="sms">SMS</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Subject (for email)</label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="Welcome to PeCashGlobal!"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Body *</label>
                    <textarea
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, body: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-32"
                      placeholder="Hello {{name}}, your authorization for ${{amount}} is ready..."
                    />
                    <p className="text-slate-500 text-xs mt-1">Use {'{{variable}}'} for placeholders</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={templateForm.is_active}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="rounded"
                    />
                    <label className="text-slate-400 text-sm">Active</label>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleCreateTemplate}
                    disabled={!templateForm.name || !templateForm.body}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Create Template
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
